import React, { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';

interface StepWrapperProps {
  nudgeMessage?: string;
  onNext: () => void;
  onBack?: () => void;
  onSkip?: () => void;
  nextLabel?: string;
  children: ReactNode;
  extraActions?: ReactNode;
}

/**
 * StepWrapper — surrounds embedded settings pages with the wizard bottom bar.
 *
 * Layout contract:
 *   - The parent (<main> in WizardShell) is `flex-1 min-h-0 overflow-hidden`
 *     so it has a computed height to scroll within.
 *   - This wrapper fills that space with flex-col.
 *   - The scrollable panel grows to fill available space above the fixed bottom bar.
 *   - The inner content panel does NOT use h-full — instead we let it size
 *     naturally so embedded pages that aren't full-height still render correctly.
 *     Scrolling is handled by the outer scrollable div.
 *
 * z-index note:
 *   The wizard shell sits at z-[48]. Radix UI portals (Sheet, Dialog, Select,
 *   Popover, AlertDialog) all use z-50, so they correctly float above the wizard
 *   when triggered from embedded settings pages.
 */
export function StepWrapper({
  nudgeMessage,
  onNext,
  onBack,
  onSkip,
  nextLabel = 'Next Step →',
  children,
  extraActions,
}: StepWrapperProps) {
  return (
    <div className="flex h-full flex-col">
      {/* Scrollable embedded content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Content panel — border + rounded, grows with content */}
        <div className="rounded-lg border border-border bg-background shadow-sm">
          {children}
        </div>
      </div>

      {/* Fixed bottom action bar */}
      <div className="shrink-0 border-t bg-background px-8 py-4">
        {nudgeMessage && (
          <p className="mb-3 text-xs text-muted-foreground leading-relaxed max-w-3xl">
            💡 {nudgeMessage}
          </p>
        )}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {onBack && (
              <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5">
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>
            )}
            {extraActions}
          </div>
          <div className="flex items-center gap-3">
            {onSkip && (
              <Button variant="ghost" size="sm" onClick={onSkip} className="text-muted-foreground">
                Skip for now
              </Button>
            )}
            <Button
              size="sm"
              onClick={onNext}
              className="px-6 text-white"
              style={{ backgroundColor: '#1a8a8a' }}
            >
              {nextLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
